import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { UserDto } from '../../../../auth/user.dto';
import { CommentComponent, CommentModalOptions } from '../../../../shared/comment/comment.component';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DocumentTitle } from '../../../../shared/models/enums/document-title.enum';
import { PaginationOptions } from '../../../../shared/pagination/pagination-options';
import { TPermission } from '../../../../shared/permissions/permissions';
import { RsjInstructionPermissionService } from '../../../../shared/permissions/services/rsj-instruction-permission.service';
import { BeneficiariesService } from '../../../../shared/services/beneficiaries.service';
import { DateService } from '../../../../shared/services/date.service';
import { RSJService } from '../../../../shared/services/rsj.service';
import { SignatureComponent, SignatureModalOptions } from '../../../../shared/signature/signature.component';
import { ToastService } from '../../../../shared/toast/toast.service';
import { InstructionRsjTraceabilityStatus } from '../../../../shared/traceability/instruction-rsj-traceability-status.enum';
import { TraceabilityDto } from '../../../../shared/traceability/traceability.dto';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { RsjInstructionNonRenewalDto } from '../dto/rsj-instruction-non-renewal.dto';
import { RsjInstructionVisibility } from '../dto/rsj-instruction-visibility.enum';
import { RsjInstructionDto } from '../dto/rsj-instruction.dto';
import { RsjInstructionCivilStatusComponent } from './rsj-instruction-civil-status/rsj-instruction-civil-status.component';
import { RsjInstructionCommitmentComponent } from './rsj-instruction-commitment/rsj-instruction-commitment.component';
import { RsjInstructionInsertionComponent } from './rsj-instruction-insertion/rsj-instruction-insertion.component';
import { RsjInstructionOtherDocumentsComponent } from './rsj-instruction-other-documents/rsj-instruction-other-documents.component';
import { RsjInstructionPaymentComponent } from './rsj-instruction-payment/rsj-instruction-payment.component';
import { RsjInstructionPersonalDataComponent } from './rsj-instruction-personal-data/rsj-instruction-personal-data.component';
import { RsjInstructionReservedDieComponent } from './rsj-instruction-reserved-die/rsj-instruction-reserved-die.component';
import { RsjInstructionResourcesComponent } from './rsj-instruction-resources/rsj-instruction-resources.component';

@Component({
  selector: 'app-rsj-details',
  templateUrl: './rsj-instruction-details.component.html',
  standalone: false,
})
export class RSJDetailsComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject();

  public rsjDetailsForm: UntypedFormGroup;
  public instructionRSJ: RsjInstructionDto;
  public beneficiary: BeneficiaryDto;
  public traceabilityStatus = InstructionRsjTraceabilityStatus;
  public DocumentTitle = DocumentTitle;

  public nonRenewals: RsjInstructionNonRenewalDto[] = [];
  public nonRenewalReasons: string[] = [];
  public filteredNonRenewals: RsjInstructionNonRenewalDto[] = [];

  public submitted: boolean = false;
  public visibility: RsjInstructionVisibility = RsjInstructionVisibility.NONE;
  public RsjInstructionVisibility = RsjInstructionVisibility;
  public commentDieVisibility: boolean = false;
  public showNonRenewal: boolean = false;

  private currentUser: UserDto;
  public canDuplicate: boolean = true;
  public displayActionsButton: boolean = true;

  constructor(
    private readonly modal: NgbActiveModal,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly toastService: ToastService,
    private readonly rsjService: RSJService,
    private readonly modalService: NgbModal,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly permissionService: RsjInstructionPermissionService,
    private readonly dateService: DateService,
  ) {}

  @ViewChild(RsjInstructionCivilStatusComponent) public rsjCivilStatus: RsjInstructionCivilStatusComponent;
  @ViewChild(RsjInstructionPersonalDataComponent) public rsjPersonalData: RsjInstructionPersonalDataComponent;
  @ViewChild(RsjInstructionResourcesComponent) public rsjResources: RsjInstructionResourcesComponent;
  @ViewChild(RsjInstructionInsertionComponent) public rsjInsertion: RsjInstructionInsertionComponent;
  @ViewChild(RsjInstructionCommitmentComponent) public rsjCommitment: RsjInstructionCommitmentComponent;
  @ViewChild(RsjInstructionPaymentComponent) public rsjPayment: RsjInstructionPaymentComponent;
  @ViewChild(RsjInstructionOtherDocumentsComponent) public rsjOtherDocuments: RsjInstructionOtherDocumentsComponent;
  @ViewChild(RsjInstructionReservedDieComponent) public rsjReservedDie?: RsjInstructionReservedDieComponent;

  public async ngOnInit(): Promise<void> {
    this.displayActionsButton = this.displayActions();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async onInit(instructionRSJ: RsjInstructionDto, user: UserDto): Promise<void> {
    this.currentUser = user;
    this.instructionRSJ = instructionRSJ;
    this.beneficiary = await this.beneficiariesService.findById(instructionRSJ.beneficiaryId);
    this.showNonRenewal =
      this.instructionRSJ?.status === InstructionRsjTraceabilityStatus.WaitingForNonRenewal ||
      this.instructionRSJ?.status === InstructionRsjTraceabilityStatus.Done;
    if (this.showNonRenewal) {
      this.nonRenewals = await this.rsjService.findNonRenewalReasons();
      this.nonRenewalReasons = this.nonRenewals.map((item) => item.reason).filter((item, index, self) => self.indexOf(item) === index);
      const nonRenewalReason: string = this.instructionRSJ?.nonRenewalId
        ? this.nonRenewals.filter((item) => item.id === this.instructionRSJ?.nonRenewalId)[0]?.reason
        : undefined;
      this.filteredNonRenewals = this.nonRenewals.filter((item) => item.reason === nonRenewalReason);
      this.rsjDetailsForm = this.formBuilder.group({
        instructionDate: [{ value: instructionRSJ.instructionDate, disabled: true }],
        directedBy: [{ value: instructionRSJ.directedBy, disabled: true }],
        nonRenewalReason: [nonRenewalReason, [Validators.required]],
        nonRenewalId: [instructionRSJ.nonRenewalId, [Validators.required]],
        nonRenewalComment: [instructionRSJ.nonRenewalComment],
      });
      this.rsjDetailsForm.controls.nonRenewalReason.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.rsjDetailsForm.controls.nonRenewalId.reset();
        this.filteredNonRenewals = this.nonRenewals.filter((item) => item.reason === value);
      });
    } else {
      this.rsjDetailsForm = this.formBuilder.group({
        instructionDate: [{ value: instructionRSJ.instructionDate, disabled: true }],
        directedBy: [{ value: instructionRSJ.directedBy, disabled: true }],
      });
    }
    this.updateFormVisibility();
    await this.checkCanDuplicate();
  }

  public async onSubmit(beforeStateChange: boolean = false): Promise<void> {
    this.submitted = true;
    const validCivilStatus: boolean = this.rsjCivilStatus?.onSubmit();
    const validPersonalData: boolean = this.rsjPersonalData?.onSubmit();
    const validResources: boolean = this.rsjResources?.onSubmit();
    const validInsertion: boolean = this.rsjInsertion?.onSubmit();
    const validCommitment: boolean = this.rsjCommitment?.onSubmit();
    const validPayment: boolean = this.rsjPayment?.onSubmit();
    const validOtherDocuments: boolean = this.rsjOtherDocuments?.onSubmit();
    const validReservedDie: boolean = this.rsjReservedDie ? this.rsjReservedDie.onSubmit() : true;
    if (
      validCivilStatus &&
      validPersonalData &&
      validResources &&
      validInsertion &&
      validCommitment &&
      validPayment &&
      validOtherDocuments &&
      validReservedDie
    ) {
      this.instructionRSJ.rsjCivilStatus = this.rsjCivilStatus.getDto();
      this.instructionRSJ.rsjPersonalData = this.rsjPersonalData.getDto();
      this.instructionRSJ.rsjResources = this.rsjResources.getDto();
      this.instructionRSJ.rsjInsertion = this.rsjInsertion.getDto();
      this.instructionRSJ.rsjCommitment = this.rsjCommitment.getDto();
      this.instructionRSJ.rsjPayment = this.rsjPayment.getDto();
      this.instructionRSJ.otherDocuments = this.rsjOtherDocuments.getDtos();
      if (this.rsjReservedDie) {
        this.instructionRSJ.rsjReservedDie = this.rsjReservedDie.getDto();
      }
      try {
        await this.rsjService.saveInstructionRSJ(this.instructionRSJ, !beforeStateChange);
        if (!beforeStateChange) {
          this.toastService.show("L'instruction a été sauvegardée avec succès.", 'success');
          this.modal.close();
        }
      } catch (error) {
        if (error.status === 437) {
          this.toastService.show("Vous n'avez pas l'autorisation d'effectuer cette action.", 'danger');
        }
      }
    } else {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    }
  }

  public async generateDocument(titleId: DocumentTitle, withComment: boolean = false): Promise<void> {
    let comment: string;
    if (withComment) {
      comment = await this.openCommentModal(undefined, true);
      if (comment === null) {
        return;
      }
    }
    return this.rsjService.generateInstructionRsjDocument(this.instructionRSJ.id, titleId, comment);
  }

  public cancel(): void {
    this.modal.close();
  }

  public async deleteRSJ(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      await this.rsjService.deleteInstructionRSJ(this.instructionRSJ.id);
      this.toastService.show("L'instruction a été supprimée avec succès.", 'success');
      this.modal.close();
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer cette instruction RSJ ?');
  }

  private async openSignatureModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(SignatureComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: SignatureComponent = modal.componentInstance as SignatureComponent;
    modalComponent.title = 'Signature bénéficiaire';
    modalComponent.setOptions({
      topText: "La signature sera utilisée pour générer l'attestation de situation ainsi que la déclaration d'engagement en cas de première demande.",
      bottomText: 'En signant, vous transmettez cette demande à la Métropole de Lyon. Elle ne sera plus modifiable à partir de ce moment-là.',
      approval: true,
      fileLink: 'assets/templates/RSJ_Declaration_Engagement.pdf',
    } as SignatureModalOptions);
    return modalComponent.confirm();
  }

  private async openCommentModal(text?: string, required: boolean = false): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(CommentComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: CommentComponent = modal.componentInstance as CommentComponent;
    modalComponent.setOptions({
      text,
      required,
    } as CommentModalOptions);
    return modalComponent.confirm();
  }

  public async syncfolderInfo(closeOnError: boolean = false): Promise<void> {
    try {
      const updatedInformations = await this.rsjService.getInstructionRSJInit(this.instructionRSJ.beneficiaryId);
      this.rsjCivilStatus.syncForm(updatedInformations.rsjCivilStatus);
      this.rsjPersonalData.syncForm(updatedInformations.rsjPersonalData);
      this.rsjInsertion.syncForm(updatedInformations.rsjInsertion);
      this.rsjCommitment.syncForm(updatedInformations.rsjCommitment);
      this.rsjPayment.updated = true;
      this.rsjResources.updated = true;
    } catch (error) {
      if (error.status === 461) {
        this.toastService.show('Veuillez compléter le dossier du bénéficiaire (Accompagnement, Orientation, Référent, Adresse...).', 'danger');
        if (closeOnError) {
          this.modal.close();
        }
      }
    }
  }

  public async workflowAction(status: InstructionRsjTraceabilityStatus): Promise<void> {
    const validForms: boolean =
      this.rsjCivilStatus?.civilStatusForm?.valid &&
      this.rsjPersonalData?.personalDataForm?.valid &&
      this.rsjResources?.resourcesForm?.valid &&
      this.rsjInsertion?.insertionForm?.valid &&
      this.rsjCommitment?.commitmentForm?.valid &&
      this.rsjPayment?.paymentForm?.valid &&
      this.rsjOtherDocuments?.otherDocumentsForm?.valid;

    if (
      (this.permissionService.getPermissions(this.currentUser, this.instructionRSJ) || []).some((permission) =>
        ['RSJ_INSTRUCTION_CAN_SAVE', 'RSJ_INSTRUCTION_CAN_SAVE_DIE'].includes(permission),
      )
    ) {
      if (
        InstructionRsjTraceabilityStatus.Analysis === status &&
        InstructionRsjTraceabilityStatus.Creation === this.instructionRSJ.status &&
        !validForms
      ) {
        this.toastService.show('Veuillez compléter tous les champs obligatoires.', 'danger');
        return;
      } else {
        await this.onSubmit(true);
      }
    }

    if (InstructionRsjTraceabilityStatus.Analysis === status) {
      if (InstructionRsjTraceabilityStatus.WaitingForInfo === this.instructionRSJ.status) {
        let comment: string = await this.openCommentModal(
          'Vous vous apprêtez à répondre à une demande d’information de la DIE. ' +
            'Si vous souhaitez importer un document, vous pouvez l’ajouter dans le volet « Autres documents nécessaires à l’instruction ». ' +
            'Avez-vous également pensé à synchroniser l’instruction avec la fiche bénéficiaire ?',
        );
        if (comment !== null) {
          await this.updateStatus(new TraceabilityDto(this.instructionRSJ.id, status, comment), "L'instruction a été sauvegardée avec succès.");
        }
      } else if (InstructionRsjTraceabilityStatus.Creation === this.instructionRSJ.status) {
        let signature: string = await this.openSignatureModal();
        if (signature) {
          await this.updateStatus(
            new TraceabilityDto(this.instructionRSJ.id, status, undefined, signature),
            "L'instruction a été sauvegardée avec succès.",
          );
        }
      }
    } else if (
      this.instructionRSJ.status === InstructionRsjTraceabilityStatus.WaitingForNonRenewal &&
      status === InstructionRsjTraceabilityStatus.Done
    ) {
      this.submitted = true;
      if (this.rsjDetailsForm.valid && (await this.saveNonRenewal())) {
        await this.updateStatus(new TraceabilityDto(this.instructionRSJ.id, status), "L'instruction a été sauvegardée avec succès.");
      }
    } else {
      let comment: string = await this.openCommentModal();
      if (comment !== null) {
        //
        await this.updateStatus(new TraceabilityDto(this.instructionRSJ.id, status, comment), "L'instruction a été sauvegardée avec succès.");
      }
    }
  }

  private displayActions(): boolean {
    const permissions: TPermission[] = this.permissionService.getPermissions(this.currentUser, this.instructionRSJ) ?? [];
    return (
      this.canDuplicate ||
      permissions.some((permission) =>
        [
          'RSJ_INSTRUCTION_CAN_SIGN',
          'RSJ_INSTRUCTION_CAN_ACCEPT',
          'RSJ_INSTRUCTION_CAN_REFUSE',
          'RSJ_INSTRUCTION_CAN_VALIDATE',
          'RSJ_INSTRUCTION_CAN_ASK_SUSPENSION',
          'RSJ_INSTRUCTION_CAN_ASK_FOR_INFO',
          'RSJ_INSTRUCTION_CAN_ACCEPT_SUSPENSION',
          'RSJ_INSTRUCTION_CAN_REFUSE_SUSPENSION',
          'RSJ_INSTRUCTION_CAN_VALIDATE_NON_RENEWAL',
        ].includes(permission),
      )
    );
  }

  private updateFormVisibility() {
    if (!this.instructionRSJ.id) {
      this.visibility = RsjInstructionVisibility.FULL;
      return;
    }
    const permissions: TPermission[] = this.permissionService.getPermissions(this.currentUser, this.instructionRSJ) || [];
    if (permissions.includes('RSJ_INSTRUCTION_CAN_SAVE')) {
      this.visibility = RsjInstructionVisibility.FULL;
    } else if (
      permissions.includes('RSJ_INSTRUCTION_CAN_SAVE_DIE') &&
      this.instructionRSJ.status === InstructionRsjTraceabilityStatus.SuspensionInProgress
    ) {
      this.rsjDetailsForm.disable({ emitEvent: false });
      this.visibility = RsjInstructionVisibility.DIE;
    } else if (
      permissions.includes('RSJ_INSTRUCTION_CAN_SAVE_DIE') &&
      (this.instructionRSJ.status === InstructionRsjTraceabilityStatus.Analysis ||
        this.instructionRSJ.status == InstructionRsjTraceabilityStatus.WaitingForInfo)
    ) {
      this.rsjDetailsForm.disable({ emitEvent: false });
      this.visibility = RsjInstructionVisibility.DIE_PAYMENTS;
    } else if (permissions.includes('RSJ_INSTRUCTION_CAN_VALIDATE_NON_RENEWAL')) {
      this.visibility = RsjInstructionVisibility.NONE;
    } else {
      this.rsjDetailsForm.disable({ emitEvent: false });
      this.visibility = RsjInstructionVisibility.NONE;
    }
    this.updateCommentaryDieVisibility(permissions);
  }

  private updateCommentaryDieVisibility(permissions: TPermission[]) {
    if (permissions.includes('RSJ_INSTRUCTION_CAN_SAVE_DIE_COMMENT')) {
      this.commentDieVisibility = true;
    } else {
      this.commentDieVisibility = false;
    }
  }

  private async saveNonRenewal(): Promise<boolean> {
    this.instructionRSJ.nonRenewalId = this.rsjDetailsForm.controls.nonRenewalId.value;
    this.instructionRSJ.nonRenewalComment = this.rsjDetailsForm.controls.nonRenewalComment.value;
    try {
      await this.rsjService.saveInstructionRSJ(this.instructionRSJ);
      return true;
    } catch (error) {
      if (error.status === 437) {
        this.toastService.show("Vous n'avez pas l'autorisation d'effectuer cette action.", 'danger');
      }
    }
  }

  private async updateStatus(traceabilityDto: TraceabilityDto, success: string): Promise<void> {
    try {
      await this.rsjService.updateStatus(traceabilityDto);
      this.modal.close();
      this.toastService.show(success, 'success');
    } catch (error) {
      if (error.status === 437) {
        this.toastService.show("Vous n'avez pas l'autorisation d'effectuer cette action.", 'danger');
      } else if (error.status === 452) {
        this.toastService.show("Le bénéficiaire ne possède pas d'informations bancaires.", 'danger');
      } else if (error.status === 453) {
        this.toastService.show('Veuillez enregistrer une signature dans votre profil.', 'danger');
      } else if (error.status === 454) {
        this.toastService.show('La proposition de versement est manquante.', 'danger');
      } else if (error.status === 455) {
        this.toastService.show('Il reste des paiements prévus pour ce bénéficiaire. Renouvellement impossible.', 'danger');
      } else if (error.status === 456) {
        this.toastService.show('Cette instruction prévoit des paiements après les 25 ans du bénéficiaire. Validation impossible.', 'danger');
      }
      //
    }
  }

  private async checkCanDuplicate() {
    let paymentData = await this.rsjService.getPayments(this.beneficiary?.id, new PaginationOptions(1, 25));
    let age = moment(this.dateService.getCurrent()).diff(this.beneficiary.birthDate, 'years');
    if (
      !(this.currentUser.groups.includes('ADMIN') || this.currentUser.groups.includes('STRUCTURE')) ||
      age >= 25 ||
      paymentData.totalItems >= 24 ||
      !(
        this.instructionRSJ.status === InstructionRsjTraceabilityStatus.PaymentInProgress ||
        this.instructionRSJ.status === InstructionRsjTraceabilityStatus.Done ||
        this.instructionRSJ.status === InstructionRsjTraceabilityStatus.Suspended ||
        this.instructionRSJ.status === InstructionRsjTraceabilityStatus.Refused
      )
    ) {
      this.canDuplicate = false;
    }
  }

  public async duplicateInstruction() {
    this.instructionRSJ = await this.rsjService.getDuplicateInstructionRSJ(this.instructionRSJ.id);
    const modal: NgbModalRef = this.modalService.open(RSJDetailsComponent, {
      windowClass: 'rightModal',
      size: 'xl',
      backdrop: 'static',
    });
    const modalComponent: RSJDetailsComponent = modal.componentInstance as RSJDetailsComponent;
    modalComponent.onInit(this.instructionRSJ, this.currentUser);
    modalComponent.syncfolderInfo(true);
    this.cancel();
  }
}
